import { type EndpointResponse } from '@owner/olympus-client'
import { useLocalStorage } from '@vueuse/core'
import { isBefore } from 'date-fns'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed } from 'vue'
import { type RouteLocationNamedRaw } from 'vue-router'

import { clearAllLaunchComposerDrafts } from '@/features/launch/composables/useLaunchComposerDrafts'
import { logError, logInfo } from '@/logger'
import { OlympusClient } from '@/OlympusClient'

import {
  type User,
  type BrandSession,
  type SessionResponse,
  type Session,
  type AuthLogoutOptions,
} from './types'
import {
  getAuthorizer,
  resetAuthorizer,
  safeInitializeAuthorizer,
} from './useAuthorizer'

export const useAuthStore = defineStore('auth', () => {
  const otpSessionId = useLocalStorage<string | null>('otp-session-id', null)

  const setOtpSessionId = (sessionId: string): void => {
    otpSessionId.value = sessionId
  }

  // Route getters to the appropriate store
  const isMultiBrandUser = useLocalStorage<boolean>(
    'owner-is-multi-brand-user',
    false
  )

  const isLocationScopedUser = computed(() => {
    if (!brandSession.value) return false

    return (
      !!brandSession.value.locationIds &&
      brandSession.value.locationIds.length > 1
    )
  })

  const hasAllLocationsAccess = computed(() => {
    if (!brandSession.value) return false

    return brandSession.value.hasAllLocationsAccess ?? false
  })

  const brandSession = useLocalStorage<BrandSession | null>(
    'owner-brand-session',
    null,
    {
      serializer: {
        read: (v) => (v ? JSON.parse(v) : null),
        write: (v) => JSON.stringify(v),
      },
    }
  )

  const csrfToken = computed<string | null>(() => {
    if (!session.value) return null

    return session.value.csrfToken ?? null
  })

  const scopes = computed(() => {
    if (!brandSession.value) return []

    return brandSession.value.scopes
  })

  const expiresAt = computed(() => {
    if (!session.value) return null

    return session.value.expires
  })

  const session = useLocalStorage<Session | null>('owner-session-v2', null, {
    serializer: {
      read: (v) => (v ? JSON.parse(v) : null),
      write: (v) => JSON.stringify(v),
    },
  })

  // New V2 user storage
  const user = useLocalStorage<User | null>('owner-user-v2', null, {
    serializer: {
      read: (v) => (v ? JSON.parse(v) : null),
      write: (v) => JSON.stringify(v),
    },
  })

  // Persisted so the session-restore path in `checkAuthenticated` re-creates
  // the same Authorizer provider chosen at login (otherwise refresh always
  // falls back to Olympus and ignores the auth-service `partner_at` cookie).
  const sessionProvider = useLocalStorage<'olympus' | 'auth-service' | null>(
    'owner-session-provider',
    null
  )

  // Login timestamp, stored separately because `Session` is a generated
  // olympus-client type we don't own. Persisted so it survives session restore
  // (refresh / new tab) and the logout funnel can always report how long the
  // session actually lasted (`sessionAgeMs`).
  const loginAt = useLocalStorage<string | null>('owner-session-login-at', null)

  const acl = computed(() => {
    if (!brandSession.value)
      return {
        brands: [],
        locations: [],
      }

    return {
      brands: brandSession.value.brandId ? [brandSession.value.brandId] : null,
      locations: brandSession.value.locationIds,
    }
  })

  const role = computed(() => {
    if (!brandSession.value) return null

    return brandSession.value.role
  })

  const isSuperAdmin = computed(() => {
    if (!role.value) return false

    return role.value === 'pb:super'
  })

  const home = computed<RouteLocationNamedRaw>(() => {
    if (!user.value) return { name: 'auth.login' }

    if (isSuperAdmin.value) return { name: 'brands' }

    if (brandSession.value?.brandId) {
      return {
        name: 'brand.home',
        params: {
          brandId: brandSession.value.brandId,
        },
      }
    }

    return { name: 'auth.login' }
  })

  const isAuthenticated = computed(() => {
    if (!user.value || !session.value) return false

    try {
      const now = new Date()
      const expires = new Date(session.value.expires)
      const isExpired = isBefore(expires, now)
      if (isExpired) {
        logError(new Error('Auth session expired'), {
          context: 'useAuthStore',
          expires,
          now,
        })
      }
      return !isExpired
    } catch (error) {
      return false
    }
  })

  // Helper to set session data from V2 response
  const setSession = ({
    csrfToken,
    defaultBrandSession,
    expiresAt,
    sessionProvider: newSessionProvider,
    user: sessionUser,
    isMultiBrandUser: newIsMultiBrandUserValue = false,
  }: SessionResponse): void => {
    session.value = {
      csrfToken,
      expires: expiresAt,
      scopes: defaultBrandSession.scopes,
    }

    user.value = sessionUser
    brandSession.value = defaultBrandSession
    isMultiBrandUser.value = newIsMultiBrandUserValue
    sessionProvider.value = newSessionProvider ?? null
    loginAt.value = new Date().toISOString()
  }

  // Seed the session from a parent app's iframe handoff (bearer mode). The
  // brand session — and with it the user's scopes/role — is filled in afterward
  // by loadBrand, so only the user and an expiry are needed to mark the user
  // authenticated. No CSRF token: bearer mode sends the token in the header.
  const setEmbeddedSession = ({
    user: sessionUser,
    expiresAt,
  }: {
    user: User
    expiresAt: string
  }): void => {
    session.value = {
      csrfToken: '',
      expires: expiresAt,
      scopes: [],
    }
    user.value = sessionUser
    isMultiBrandUser.value = false
    sessionProvider.value = 'olympus'
    loginAt.value = new Date().toISOString()
  }

  const setBrandSession = (newBrandSession: BrandSession): void => {
    brandSession.value = newBrandSession
  }

  // Re-initialize the Authorizer for the current brand session. The Authorizer
  // bakes the brandId into its provider at construction time, so on brand
  // switch we must destroy the old instance and build a new one.
  const reinitializeAuthorizer = async (context: string): Promise<void> => {
    if (!user.value || !brandSession.value) return

    resetAuthorizer()
    await safeInitializeAuthorizer(
      user.value,
      brandSession.value,
      context,
      sessionProvider.value ?? undefined,
      () => csrfToken.value
    )
  }

  // The active brand is persisted in localStorage and reused on every session
  // restore (refresh / new tab). If the user's access to that brand was revoked
  // since it was written, redirecting to it would fire `/core` and 403 on every
  // boot (tripping the RBAC monitor). Once the authorizer has loaded the
  // caller's access for the persisted brand, drop the brand if it is no longer
  // readable and fall back to an accessible one — the same list the brand
  // picker uses. No-op, and no extra request, on the happy path where the
  // persisted brand is still accessible.
  //
  // Returns true when the brand session was swapped or cleared, so the caller
  // can redirect to the resolved `home` instead of letting navigation continue
  // to the stale `/brands/<id>/...` URL.
  const ensureAccessibleBrandSession = async (): Promise<boolean> => {
    if (!user.value || !brandSession.value) return false

    const authorizer = getAuthorizer()
    // Authorizer failed to initialize (logged upstream) — leave the session
    // alone so the existing `/core` error handling can recover.
    if (!authorizer) return false

    try {
      if (authorizer.can('read', 'brand')) return false
    } catch {
      // Instance present but not initialized; don't disturb a possibly-valid
      // session on a transient init failure.
      return false
    }

    const staleBrandId = brandSession.value.brandId

    let fallbackBrand
    try {
      const response =
        await OlympusClient.users.v1.users.user.brands.paginateBrands({
          params: { userId: user.value.id },
          query: {
            highestSubscriptionTier: ['standard', 'website', 'none'],
            limit: 1,
            sort: ['updatedAt:desc'],
          },
        })
      fallbackBrand = response.results[0]
    } catch (error) {
      // Keep the stale session so the existing `/core` error handling can
      // recover, rather than logging the user out on a transient failure.
      logError(error, { context: 'ensureAccessibleBrandSession' })
      return false
    }

    // The persisted brand is itself the most-recently-updated accessible
    // brand — nothing to recover. Signalling a redirect here would bounce to a
    // `home` that resolves to this same brand while the authorizer is still
    // bound to the pre-swap context, so `can('read','brand')` stays false and
    // the guard re-enters indefinitely (CORE-330). Treat a no-op swap as a
    // no-op: no redirect, no event.
    if (fallbackBrand?.id === staleBrandId) return false

    logInfo('auth.stale_brand_session.recovered', {
      cleared: !fallbackBrand,
      fallbackBrandId: fallbackBrand?.id ?? null,
      staleBrandId,
    })
    // Point the session at an accessible brand; `loadBrand` repopulates the
    // full session (role / scopes / locations) from `/core` on navigation.
    // No accessible brand at all → clear so `home` routes to login.
    brandSession.value = fallbackBrand
      ? { ...brandSession.value, brandId: fallbackBrand.id }
      : null

    // Re-bind the authorizer to the resolved brand. The caller redirects to
    // `home` and re-runs this guard before `loadBrand` reinitializes the
    // authorizer, so without this the next `can('read','brand')` still reflects
    // the inaccessible brand and re-enters recovery. Best-effort: the swap is
    // already committed and `loadBrand` rebuilds the authorizer on arrival, so
    // a rebuild failure here must not undo the swap or block the redirect.
    if (fallbackBrand) {
      await reinitializeAuthorizer('ensureAccessibleBrandSession').catch(
        (error) => {
          logError(error, { context: 'ensureAccessibleBrandSession' })
        }
      )
    }
    return true
  }

  const setUserPhone = (phone: string): void => {
    if (user.value) {
      user.value = {
        ...user.value,
        phone,
      }
    }
  }

  const setUser = (userUpdate: Partial<User>): void => {
    if (user.value) {
      user.value = {
        ...user.value,
        ...userUpdate,
      }
    }
  }

  // Route actions to the appropriate store
  const login = async ({
    email,
    password,
  }: {
    email: string
    password: string
  }): Promise<
    | { status: 'success' }
    | Extract<
        EndpointResponse<'auth.v2.hermes.login'>,
        { status: 'requires-2fa' }
      >
  > => {
    const response = await OlympusClient.auth.v2.hermes.login({
      headers: {
        Authorization: `Basic ${btoa(`${email}:${password}`)}`,
      },
    })

    if (response.status === 'success') {
      setSession(response)
      await safeInitializeAuthorizer(
        response.user,
        response.defaultBrandSession,
        'login',
        response.sessionProvider,
        () => csrfToken.value
      )

      return { status: 'success' }
    } else if (response.status === 'requires-2fa') {
      otpSessionId.value = response.sessionId

      return response
    } else {
      throw new Error('Login failed')
    }
  }

  const resendOtp = async ({
    sessionId,
    method,
    mfaToken,
  }: {
    sessionId?: string | null
    method: 'email' | 'phone'
    mfaToken?: string
  }): Promise<
    Extract<EndpointResponse<'auth.v2.hermes.resendOtp'>, { status: 'success' }>
  > => {
    if (!sessionId) sessionId = otpSessionId.value

    if (!sessionId) throw new Error('Session ID is required for V2 OTP resend')

    const response = await OlympusClient.auth.v2.hermes.resendOtp({
      body: { method, mfaToken, sessionId },
    })

    if (response.status === 'success') {
      otpSessionId.value = response.sessionId
      return response
    } else {
      throw new Error(response.message)
    }
  }

  const resetPassword = async ({
    password,
    token,
  }: {
    password: string
    token: string
  }): Promise<void> => {
    // Use the V2 resetPassword endpoint
    const response = await OlympusClient.auth.v2.hermes.resetPassword({
      body: {
        password,
      },
      headers: {
        Authorization: `Otp ${token}`,
      },
    })

    // Set the session using the V2 response format
    setSession({
      ...response,
      status: 'success',
    })
    await safeInitializeAuthorizer(
      response.user,
      response.defaultBrandSession,
      'resetPassword',
      response.sessionProvider,
      () => csrfToken.value
    )
  }

  const multiFactorLogin = async ({
    sessionId,
    code,
    mfaToken,
  }: {
    sessionId?: string | null
    code: string
    mfaToken?: string
  }): Promise<void> => {
    if (!sessionId) sessionId = otpSessionId.value

    if (!sessionId)
      throw new Error(
        'Session ID is required for V2 multi-factor authentication'
      )

    const response = await OlympusClient.auth.v2.hermes.verifyOtp({
      body: { mfaToken },
      headers: {
        Authorization: `Otp ${btoa(`${code}:${sessionId}`)}`,
      },
    })

    setSession({
      ...response,
      status: 'success',
    })
    await safeInitializeAuthorizer(
      response.user,
      response.defaultBrandSession,
      'multiFactorLogin',
      response.sessionProvider,
      () => csrfToken.value
    )

    otpSessionId.value = null
  }

  // Deduplicates concurrent logout calls (e.g. multiple 401s on page load)
  let logoutInProgress: Promise<void> | null = null

  const logout = async (options?: AuthLogoutOptions): Promise<void> => {
    if (logoutInProgress) {
      // Rejection handling is the primary caller's responsibility
      await logoutInProgress.catch(() => undefined)
      return
    }

    logoutInProgress = (async () => {
      // Single chokepoint every logout passes through: record why it happened
      // and how long the session lasted, before the session is torn down.
      // `sessionAgeMs` is the headline signal for "logged out too early"
      // reports — it needs no client-side repro.
      const now = new Date()
      const expiresValue = session.value?.expires ?? null
      const loginValue = loginAt.value
      logInfo('auth.logout', {
        // Diagnostic context first so the core telemetry fields below stay
        // authoritative — a stray `reason`/`userId`/`sessionAgeMs` in `detail`
        // must never clobber the headline metrics this event exists to produce.
        ...options?.detail,
        brandId: brandSession.value?.brandId ?? null,
        expired: expiresValue ? isBefore(new Date(expiresValue), now) : null,
        expiresAt: expiresValue,
        loginAt: loginValue,
        path:
          typeof window !== 'undefined' ? window.location?.pathname : undefined,
        reason: options?.reason ?? 'unknown',
        sessionAgeMs: loginValue
          ? now.getTime() - new Date(loginValue).getTime()
          : null,
        sessionProvider: sessionProvider.value,
        userId: user.value?.id ?? null,
      })

      resetAuthorizer()

      // Capture the CSRF token before clearing the session so the logout
      // request can still authenticate, but unset the session first to avoid
      // re-entrant logout calls triggered by in-flight requests observing a
      // still-valid session (see PR #36913 logout storm).
      const csrfTokenValue = csrfToken.value

      session.value = null
      user.value = null
      brandSession.value = null
      isMultiBrandUser.value = false
      otpSessionId.value = null
      sessionProvider.value = null
      loginAt.value = null

      // Module-level draft store survives client-side navigation, so clear it
      // here or the next specialist on this tab would inherit unsent drafts.
      clearAllLaunchComposerDrafts()

      await OlympusClient.auth.v2.hermes
        .logout({
          headers: {
            'X-CSRFToken': csrfTokenValue ?? undefined,
          },
        })
        .catch(() => undefined)
    })()

    try {
      await logoutInProgress
    } finally {
      logoutInProgress = null
    }
  }

  const requireUser = (): User => {
    if (!user.value) {
      throw new Error('User not found')
    }

    return user.value
  }

  return {
    acl,
    brandSession,
    csrfToken,
    ensureAccessibleBrandSession,
    expiresAt,
    hasAllLocationsAccess,
    home,
    isAuthenticated,
    isLocationScopedUser,
    isMultiBrandUser,
    isSuperAdmin,
    login,
    logout,
    multiFactorLogin,
    reinitializeAuthorizer,
    requireUser,
    resendOtp,
    resetPassword,
    role,
    scopes,
    sessionProvider,
    setBrandSession,
    setEmbeddedSession,
    setOtpSessionId,
    setSession,
    setUser,
    setUserPhone,
    user,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
